import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';

import { PageTitle } from '../../../_metronic/layout/core';
import { authUserSelector } from '../../modules/auth';
import {
  BillingDashboard,
  authorizedBillingDashboardUsers,
} from '../../modules/billing-dashboard';
import { BusinessDashboard } from '../../modules/dashboard';
import SalesReport from '../../modules/dashboard/components/SalesReport';
import OrdersReport from '../../modules/dashboard/components/OrdersReport';
import UserKpiReport from '../../modules/dashboard/components/UserKpiReport';
import { GiftPrevision } from '../../modules/loyalty';
import RecipeSalesReport from '../../modules/dashboard/components/RecipeSalesReport';
import OrdersWeightReport from '../../modules/dashboard/components/OrdersWeightReport';
import ExcelReportForm from '../../modules/dashboard/components/ExcelReportForm';
import OriginReport from '../../modules/dashboard/components/OriginReport';
import SubscriberCohorts from '../../modules/dashboard/components/subscriber-cohorts/SubscriberCohorts';

type TabKey = 'sales' | 'procurement' | 'capture' | 'cohorts' | 'billing';

interface Tab {
  key: TabKey;
  labelId: string;
}

const TABS: Tab[] = [
  { key: 'sales', labelId: 'businessDashboard.section.salesRevenue' },
  { key: 'procurement', labelId: 'businessDashboard.section.procurementOps' },
  { key: 'capture', labelId: 'businessDashboard.section.captureSubscribers' },
  { key: 'cohorts', labelId: 'subscriberCohorts.tab' },
];
// Only for the users in authorizedBillingDashboardUsers.
const BILLING_TAB: Tab = {
  key: 'billing',
  labelId: 'billingDashboard.tab',
};

const BusinessDashboardPage: FC = () => {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector, shallowEqual);
  const [activeTab, setActiveTab] = useState<TabKey>('sales');
  const tabs = authorizedBillingDashboardUsers.includes(authUser?.email)
    ? [...TABS, BILLING_TAB]
    : TABS;

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'businessDashboard.title' })}
      </PageTitle>

      <ul className="nav nav-tabs mb-0">
        {tabs.map((tab) => (
          <li key={tab.key} className="nav-item">
            <a
              className={`nav-link ${activeTab === tab.key ? 'active fw-bolder' : ''} fs-4`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(tab.key);
              }}
            >
              {intl.formatMessage({ id: tab.labelId })}
            </a>
          </li>
        ))}
      </ul>

      <div className="bg-white border border-top-0 rounded-bottom p-8">
        <div className="row gy-4">
          {activeTab === 'sales' && (
            <>
              <div className="card shadow-sm p-10">
                <SalesReport />
              </div>
              <div className="card shadow-sm p-10">
                <BusinessDashboard />
              </div>
            </>
          )}

          {activeTab === 'procurement' && (
            <>
              <div className="card shadow-sm p-10">
                <RecipeSalesReport />
              </div>
              <div className="card shadow-sm p-10">
                <GiftPrevision />
              </div>
              <div className="card shadow-sm p-10">
                <OrdersWeightReport />
              </div>
              <div className="card shadow-sm p-10">
                <OrdersReport />
              </div>
            </>
          )}

          {activeTab === 'capture' && (
            <>
              <div className="card shadow-sm p-10">
                <UserKpiReport />
              </div>
              <div className="card shadow-sm p-10">
                <OriginReport />
              </div>
              <div className="card shadow-sm p-10">
                <ExcelReportForm />
              </div>
            </>
          )}

          {activeTab === 'cohorts' && (
            <div className="card shadow-sm p-10">
              <SubscriberCohorts />
            </div>
          )}

          {activeTab === 'billing' && <BillingDashboard />}
        </div>
      </div>
    </>
  );
};

export { BusinessDashboardPage };
